import { useCallback, useMemo, useState } from "react";
import type { MfgRecipeSummary } from "@/lib/manufacturing/types";

/** Multi-select for Models list bulk meta edit. Independent of detail `selectedId`. */
export function useManufacturingModelsBulkSelection(
  recipes: readonly MfgRecipeSummary[],
  filtered: readonly MfgRecipeSummary[],
) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [editOpen, setEditOpen] = useState(false);

  const toggleSelected = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectAllFiltered = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const r of filtered) next.add(r.id);
      return next;
    });
  }, [filtered]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setEditOpen(false);
  }, []);

  const allFilteredSelected =
    filtered.length > 0 && filtered.every((r) => selectedIds.has(r.id));

  const selectedRecipes = useMemo(() => {
    if (selectedIds.size === 0) return [] as MfgRecipeSummary[];
    const byId = new Map(recipes.map((r) => [r.id, r] as const));
    const out: MfgRecipeSummary[] = [];
    for (const id of selectedIds) {
      const r = byId.get(id);
      if (r) out.push(r);
    }
    return out;
  }, [recipes, selectedIds]);

  return {
    selectedIds,
    selectedCount: selectedIds.size,
    selectedRecipes,
    toggleSelected,
    selectAllFiltered,
    clearSelection,
    allFilteredSelected,
    editOpen,
    setEditOpen,
  };
}
